// Right-side Notes peek panel: 360 notes, + quote staff notes, or everything (calls, emails, activities). Quick add with voice.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, StickyNote, FileText, PhoneCall, Mail, Activity, Loader2, ExternalLink, Send } from "lucide-react";
import api from "../../lib/api";
import VoiceNoteButton from "../../pages/quotes/panels/VoiceNoteButton";

const KIND = {
  note: { icon: StickyNote, tone: "text-yellow-300 border-yellow-500/30 bg-yellow-500/10", label: "Note" },
  quote_note: { icon: FileText, tone: "text-orange-300 border-orange-500/30 bg-orange-500/10", label: "Quote note" },
  activity: { icon: Activity, tone: "text-purple-300 border-purple-500/30 bg-purple-500/10", label: "Activity" },
  call: { icon: PhoneCall, tone: "text-sky-300 border-sky-500/30 bg-sky-500/10", label: "Call" },
  email: { icon: Mail, tone: "text-blue-300 border-blue-500/30 bg-blue-500/10", label: "Email" },
};
const TABS = [["notes", "360 notes", ["note"]], ["withquotes", "+ Quote notes", ["note", "quote_note"]], ["all", "Everything", null]];
const when = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(+d) ? "—" : d.toLocaleString("en-GB", { day: "numeric", month: "short", year: d.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined, hour: "2-digit", minute: "2-digit" });
};

function Item({ it, i }) {
  const [open, setOpen] = useState(false);
  const M = KIND[it.kind] || KIND.note; const Icon = M.icon;
  const long = (it.body || "").length > 420;
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-3" data-testid={`notes-peek-item-${i}`} data-kind={it.kind}>
      <div className="flex items-center gap-2 text-[11px]">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${M.tone}`}><Icon className="w-3 h-3" />{M.label}</span>
        {it.kind === "quote_note" && it.quote_id
          ? <Link to={`/quotes/${it.quote_id}/edit`} className="font-mono text-orange-300 hover:underline">{it.source}</Link>
          : <span className="text-slate-500 truncate">{it.source}</span>}
        <span className="ml-auto text-slate-500 font-mono whitespace-nowrap">{when(it.ts)}</span>
      </div>
      {it.title && <div className="mt-1.5 text-[13px] font-medium text-slate-100">{it.title}</div>}
      {it.body && <div className={`mt-1.5 text-[13px] leading-relaxed text-slate-300 whitespace-pre-line break-words ${long && !open ? "line-clamp-6" : ""}`}>{it.body}</div>}
      <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500">
        {it.by && <span>by {it.by}</span>}
        {it.system && <span className="font-mono text-slate-600">system</span>}
        {long && <button onClick={() => setOpen((o) => !o)} className="ml-auto text-teal-300 hover:underline" data-testid={`notes-peek-more-${i}`}>{open ? "Show less" : "Show more"}</button>}
      </div>
    </div>
  );
}

export default function NotesPeekPanel({ peek, onClose, onCount, onOpen360 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("withquotes");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const pid = peek?.id;
  const load = async (id) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/crm/persons/${id}/notes-peek`);
      setData(d); onCount?.(id, (d.counts?.note || 0) + (d.counts?.quote_note || 0));
    } catch { toast.error("Couldn't load notes"); setData(null); } finally { setLoading(false); }
  };
  useEffect(() => { setData(null); setDraft(""); if (pid) load(pid); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pid) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [pid, onClose]);
  const kinds = TABS.find((t) => t[0] === tab)[2];
  const items = useMemo(() => (data?.items || []).filter((it) => !kinds || kinds.includes(it.kind)), [data, kinds]);
  const cnt = (ks) => (data?.items || []).filter((it) => !ks || ks.includes(it.kind)).length;
  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try { await api.post(`/crm/persons/${pid}/notes`, { content: draft.trim() }); setDraft(""); toast.success("Note added to the 360"); await load(pid); }
    catch { toast.error("Couldn't save the note"); } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} className={`fixed inset-0 z-[60] bg-black/30 transition-opacity duration-200 ${pid ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid="notes-peek-backdrop" />
      <aside className={`fixed top-0 right-0 z-[61] h-full w-full sm:w-[520px] bg-[#0B1220] border-l border-white/10 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${pid ? "translate-x-0" : "translate-x-full"}`}
        data-testid="notes-peek-panel" aria-hidden={!pid}>
        <div className="px-5 pt-5 pb-3 border-b border-white/[0.07]">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest font-mono text-yellow-300/80">Notes peek</div>
              <div className="text-lg font-semibold text-white truncate" data-testid="notes-peek-name">{data?.person?.name || peek?.name || "…"}</div>
              {data?.person?.company && <div className="text-[12px] text-slate-400 truncate">{data.person.company}</div>}
            </div>
            {onOpen360 && pid && <button onClick={() => { onOpen360(pid); onClose(); }} data-testid="notes-peek-open-360"
              className="px-2.5 py-1.5 rounded-md border border-teal-400/40 bg-teal-500/10 text-teal-200 text-[11.5px] inline-flex items-center gap-1 hover:bg-teal-500/20"><ExternalLink className="w-3 h-3" />Open 360</button>}
            <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5" data-testid="notes-peek-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-1.5 mt-3">
            {TABS.map(([k, l, ks]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`notes-peek-tab-${k}`}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${tab === k ? "bg-yellow-500/15 border-yellow-400/50 text-yellow-100" : "border-white/10 text-slate-400 hover:border-white/25"}`}>
                {l} <span className="font-mono opacity-70">{data ? cnt(ks) : ""}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-b border-white/[0.07] bg-white/[0.015]" data-testid="notes-peek-add">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Add a note to this customer's 360…" data-testid="notes-peek-input"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); }}
            className="w-full resize-y rounded-md bg-black/30 border border-white/10 focus:border-yellow-400/50 outline-none px-3 py-2 text-[13px] text-slate-100" />
          <div className="flex items-center gap-2 mt-2">
            <VoiceNoteButton url="/ai/transcribe" testid="notes-peek-voice" label="Speak it"
              onResult={({ raw, clean }) => { setDraft((d) => [d, clean || raw].filter(Boolean).join("\n")); toast.success("Transcribed — check it, then save"); }} />
            <span className="text-[10.5px] text-slate-500">Ctrl/⌘ + Enter to save</span>
            <button onClick={save} disabled={!draft.trim() || saving} data-testid="notes-peek-save"
              className="ml-auto px-3 py-1.5 rounded-md bg-yellow-400 text-black text-[12px] font-semibold inline-flex items-center gap-1 disabled:opacity-40">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}Add note
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5" data-testid="notes-peek-list">
          {loading && !data && <div className="py-10 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading notes…</div>}
          {data && items.length === 0 && <div className="py-10 text-center text-slate-500 text-[13px]" data-testid="notes-peek-empty">Nothing here yet.</div>}
          {items.map((it, i) => <Item key={`${it.kind}-${it.ts}-${i}`} it={it} i={i} />)}
        </div>
      </aside>
    </>
  );
}
